import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { cn } from '@/lib/cn'

export type TabItem<T extends string> = { value: T; label: string; disabled?: boolean }

/** Переключатель-сегменты: периоды графика, режимы экрана. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  size = 'md',
  className,
}: {
  items: readonly TabItem<T>[]
  value: T
  onChange: (value: T) => void
  /** Подпись группы для экранного диктора. */
  label?: string
  size?: 'sm' | 'md'
  className?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn('inline-flex max-w-full overflow-x-auto rounded-lg bg-surface-2 p-0.5', className)}
    >
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-3 font-medium transition-colors',
              size === 'sm' ? 'min-h-9 text-xs lg:min-h-7' : 'min-h-10 text-sm lg:min-h-8',
              active ? 'bg-surface text-fg shadow-sm' : 'text-muted hover:text-fg',
              'disabled:opacity-40',
            )}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

/** Вкладки-ссылки (подразделы: /settings/security, /health/weight …). На телефоне полоса листается
 * вбок: активная вкладка прокручивается в середину, у края с продолжением — затухание. */
export function TabLinks({
  items,
  className,
}: {
  items: readonly { path: string; label: string }[]
  className?: string
}) {
  const ref = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const [edges, setEdges] = useState({ left: false, right: false })

  useEffect(() => {
    const nav = ref.current
    if (!nav) return
    const update = () =>
      setEdges({ left: nav.scrollLeft > 2, right: nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 2 })
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (active) nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2
    update()
    nav.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      nav.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [pathname])

  const fade = edges.left && edges.right ? 'both' : edges.left ? 'left' : edges.right ? 'right' : 'none'
  return (
    <nav
      ref={ref}
      data-fade={fade}
      className={cn(
        'relative -mx-4 overflow-x-auto px-4 [scrollbar-width:none] lg:mx-0 lg:px-0 [&::-webkit-scrollbar]:hidden',
        'data-[fade=both]:[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]',
        'data-[fade=left]:[mask-image:linear-gradient(to_right,transparent,black_2rem)]',
        'data-[fade=right]:[mask-image:linear-gradient(to_left,transparent,black_2rem)]',
        className,
      )}
      aria-label="Подразделы"
    >
      <div className="flex min-w-max gap-1 border-b border-border">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                '-mb-px flex min-h-11 items-center whitespace-nowrap border-b-2 px-3 text-sm font-medium lg:min-h-10',
                isActive
                  ? 'border-accent text-fg'
                  : 'border-transparent text-muted hover:text-fg',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}
